import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "raviger";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/input-password";
import { Label } from "@/components/ui/label";

import { LocalStorageKeys } from "@/common/constants";

import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import { cn } from "@/lib/utils";
import saasApi, {
  SaasClinicType,
  SaasPlan,
  SaasSignupRequest,
  SaasSignupResponse,
} from "@/types/saas/saasApi";

const STEPS = ["clinic_type", "plan", "details"] as const;

type SignupForm = {
  organization_name: string;
  facility_name: string;
  address: string;
  pincode: string;
  facility_phone: string;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone_number: string;
  password: string;
};

const emptyForm: SignupForm = {
  organization_name: "",
  facility_name: "",
  address: "",
  pincode: "",
  facility_phone: "",
  first_name: "",
  last_name: "",
  username: "",
  email: "",
  phone_number: "",
  password: "",
};

function formatPrice(plan: SaasPlan) {
  const amount = Number(plan.monthly_price);
  if (!amount) return "Free";
  return `${plan.currency === "INR" ? "₹" : plan.currency + " "}${amount.toLocaleString("en-IN")}`;
}

export default function Signup() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<(typeof STEPS)[number]>("clinic_type");
  const [clinicType, setClinicType] = useState<string>("");
  const [planCode, setPlanCode] = useState<string>("");
  const [form, setForm] = useState<SignupForm>(emptyForm);
  const [errors, setErrors] = useState<
    Partial<Record<keyof SignupForm, string>>
  >({});

  const { data: clinicTypes } = useQuery({
    queryKey: ["saas", "clinic-types"],
    queryFn: query(saasApi.clinicTypes),
  });
  const { data: plans } = useQuery({
    queryKey: ["saas", "plans"],
    queryFn: query(saasApi.plans),
  });

  const selectedClinic = useMemo(
    () => clinicTypes?.results.find((c) => c.code === clinicType),
    [clinicTypes, clinicType],
  );
  const selectedPlan = useMemo(
    () => plans?.results.find((p) => p.code === planCode),
    [plans, planCode],
  );

  const { mutate: signup, isPending } = useMutation({
    mutationFn: mutate(saasApi.signup),
    onSuccess: async (data: SaasSignupResponse) => {
      localStorage.setItem(LocalStorageKeys.accessToken, data.access);
      localStorage.setItem(LocalStorageKeys.refreshToken, data.refresh);
      await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      toast.success(t("saas_signup_success", { name: data.tenant.name }));
      window.location.href = `/facility/${data.facility.id}/overview`;
    },
  });

  const chooseClinic = (c: SaasClinicType) => {
    setClinicType(c.code);
    setPlanCode(c.recommended_plan);
    setStep("plan");
  };

  const update = (key: keyof SignupForm, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const next: Partial<Record<keyof SignupForm, string>> = {};
    (Object.keys(emptyForm) as (keyof SignupForm)[]).forEach((key) => {
      if (!form[key].trim()) next[key] = t("field_required");
    });
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) {
      next.pincode = t("saas_invalid_pincode");
    }
    if (form.password && form.password.length < 8) {
      next.password = t("saas_password_too_short");
    }
    if (form.username && !/^[A-Za-z0-9_-]+$/.test(form.username)) {
      next.username = t("saas_invalid_username");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const body: SaasSignupRequest = {
      organization_name: form.organization_name,
      clinic_type: clinicType,
      plan: planCode,
      facility: {
        name: form.facility_name,
        address: form.address,
        pincode: Number(form.pincode),
        phone_number: form.facility_phone,
      },
      admin: {
        username: form.username,
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        phone_number: form.phone_number,
        gender: "non_binary",
      },
    };
    signup(body);
  };

  const field = (
    key: keyof SignupForm,
    label: string,
    props: Partial<React.ComponentProps<typeof Input>> = {},
  ) => (
    <div className="space-y-1">
      <Label htmlFor={key}>{label}</Label>
      <Input
        id={key}
        name={key}
        value={form[key]}
        onChange={(e) => update(key, e.target.value)}
        {...props}
      />
      {errors[key] && <p className="text-xs text-red-600">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <Link href="/">
            <img
              src="/images/diigoo_logo.svg"
              alt="DIIgoo HEALTH logo"
              className="h-14 w-auto"
            />
          </Link>
          <Link href="/login" className="text-sm text-primary-700 underline">
            {t("saas_already_have_account")}
          </Link>
        </div>

        <div className="flex gap-2 text-sm">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={cn(
                "flex items-center gap-2 rounded-full px-3 py-1",
                step === s
                  ? "bg-primary-600 text-white"
                  : "bg-white text-gray-600 border",
              )}
            >
              <span className="font-semibold">{i + 1}</span>
              {t(`saas_step_${s}`)}
            </div>
          ))}
        </div>

        {step === "clinic_type" && (
          <div>
            <h1 className="mb-1 text-2xl font-bold">
              {t("saas_pick_clinic_type")}
            </h1>
            <p className="mb-4 text-gray-600">
              {t("saas_pick_clinic_type_help")}
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {clinicTypes?.results.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => chooseClinic(c)}
                  className={cn(
                    "rounded-lg border bg-white p-4 text-left transition hover:border-primary-500 hover:shadow",
                    clinicType === c.code &&
                      "border-primary-600 ring-2 ring-primary-200",
                  )}
                >
                  <div className="font-semibold">{c.name}</div>
                  <div className="mt-1 text-sm text-gray-600">
                    {c.description}
                  </div>
                  <div className="mt-3 text-xs text-gray-500">
                    {c.departments.length > 0 &&
                      t("saas_departments_count", {
                        count: c.departments.length,
                      })}
                    {c.departments.length > 0 && " · "}
                    {t("saas_locations_count", { count: c.locations.length })}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "plan" && (
          <div>
            <h1 className="mb-1 text-2xl font-bold">{t("saas_pick_plan")}</h1>
            <p className="mb-4 text-gray-600">
              {t("saas_pick_plan_help", {
                clinic: selectedClinic?.name ?? "",
              })}
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {plans?.results.map((p) => (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => setPlanCode(p.code)}
                  className={cn(
                    "flex flex-col rounded-lg border bg-white p-4 text-left transition hover:border-primary-500 hover:shadow",
                    planCode === p.code &&
                      "border-primary-600 ring-2 ring-primary-200",
                  )}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-semibold">{p.name}</span>
                    {selectedClinic?.recommended_plan === p.code && (
                      <span className="rounded bg-primary-100 px-2 py-0.5 text-xs text-primary-800">
                        {t("saas_recommended")}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-2xl font-bold">
                    {formatPrice(p)}
                    <span className="text-sm font-normal text-gray-500">
                      {" "}
                      {t("saas_per_month")}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">{p.tagline}</div>
                  <ul className="mt-3 space-y-1 text-xs text-gray-600">
                    <li>
                      {p.max_users === null
                        ? t("saas_unlimited_users")
                        : t("saas_up_to_users", { count: p.max_users })}
                    </li>
                    <li>
                      {p.max_facilities === null
                        ? t("saas_unlimited_facilities")
                        : t("saas_up_to_facilities", {
                            count: p.max_facilities,
                          })}
                    </li>
                    <li>{t("saas_trial_days", { count: p.trial_days })}</li>
                  </ul>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.modules.map((m) => (
                      <span
                        key={m}
                        className="rounded bg-gray-100 px-2 py-0.5 text-[11px] uppercase tracking-wide text-gray-700"
                      >
                        {t(`saas_module_${m}`)}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-6 flex justify-between">
              <Button variant="outline" onClick={() => setStep("clinic_type")}>
                {t("back")}
              </Button>
              <Button
                variant="primary"
                disabled={!planCode}
                onClick={() => setStep("details")}
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        )}

        {step === "details" && (
          <form onSubmit={submit} className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t("saas_organization_details")}</CardTitle>
                  <CardDescription>
                    {t("saas_organization_details_help")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  {field("organization_name", t("saas_organization_name"))}
                  {field("facility_name", t("saas_facility_name"))}
                  <div className="sm:col-span-2">
                    {field("address", t("address"))}
                  </div>
                  {field("pincode", t("pincode"), { inputMode: "numeric" })}
                  {field("facility_phone", t("saas_facility_phone"), {
                    placeholder: "+91XXXXXXXXXX",
                  })}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t("saas_admin_details")}</CardTitle>
                  <CardDescription>
                    {t("saas_admin_details_help")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  {field("first_name", t("first_name"))}
                  {field("last_name", t("last_name"))}
                  {field("username", t("username"), {
                    autoComplete: "username",
                  })}
                  {field("email", t("email"), { type: "email" })}
                  {field("phone_number", t("phone_number"), {
                    placeholder: "+91XXXXXXXXXX",
                  })}
                  <div className="space-y-1">
                    <Label htmlFor="password">{t("password")}</Label>
                    <PasswordInput
                      id="password"
                      name="password"
                      value={form.password}
                      onChange={(e) => update("password", e.target.value)}
                    />
                    {errors.password && (
                      <p className="text-xs text-red-600">{errors.password}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep("plan")}
                >
                  {t("back")}
                </Button>
                <Button type="submit" variant="primary" disabled={isPending}>
                  {isPending ? t("saas_creating") : t("saas_create_account")}
                </Button>
              </div>
            </div>
            <Card className="h-fit">
              <CardHeader>
                <CardTitle>{t("saas_summary")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <div className="text-gray-500">
                    {t("saas_step_clinic_type")}
                  </div>
                  <div className="font-medium">{selectedClinic?.name}</div>
                </div>
                <div>
                  <div className="text-gray-500">{t("saas_step_plan")}</div>
                  <div className="font-medium">
                    {selectedPlan?.name} ·{" "}
                    {selectedPlan && formatPrice(selectedPlan)}{" "}
                    {t("saas_per_month")}
                  </div>
                </div>
                {selectedClinic && (
                  <div>
                    <div className="text-gray-500">
                      {t("saas_will_be_created")}
                    </div>
                    <ul className="mt-1 list-disc pl-4 text-gray-700">
                      {selectedClinic.departments.length > 0 && (
                        <li>
                          {t("saas_departments_count", {
                            count: selectedClinic.departments.length,
                          })}
                        </li>
                      )}
                      <li>
                        {t("saas_locations_count", {
                          count: selectedClinic.locations.length,
                        })}
                      </li>
                      {selectedClinic.services.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  {t("saas_trial_note", {
                    count: selectedPlan?.trial_days ?? 14,
                  })}
                </p>
              </CardContent>
            </Card>
          </form>
        )}
      </div>
    </div>
  );
}
